/**
 * Client half of desktop sign-in by phone approval.
 *
 * Three thin wrappers over the callables in functions/src/auth/deviceLink.js, which
 * is where the design and the security reasoning live. The only thing worth knowing
 * on this side: `claimSecret` is returned exactly once, by createSession, and must
 * stay in the calling component's memory. Persisting it anywhere a second tab or a
 * later visitor could read it would undo the reason the token is not simply sitting
 * in Firestore.
 */

import firebaseApp from "services/firebase";

const CREATE_FUNCTION = "createDeviceLinkSession";
const APPROVE_FUNCTION = "approveDeviceLinkSession";
const CLAIM_FUNCTION = "claimDeviceLinkSession";
const CREATE_GRANT_FUNCTION = "createDeviceLinkGrant";
const REDEEM_GRANT_FUNCTION = "redeemDeviceLinkGrant";

// Fast enough that the desktop feels like it reacted to the tap, slow enough that a
// forgotten tab is not hammering a callable. The session expires in two minutes, so
// this runs at most ~60 times.
export const DEVICE_LINK_POLL_MS = 2000;

/** The URL that goes in the QR code. Absolute — it is opened on a different device. */
export const buildDeviceLinkUrl = (sessionId) =>
  `${window.location.origin}/link-device?c=${encodeURIComponent(sessionId)}`;

export const createDeviceLinkSession = async () => {
  const callable = firebaseApp.functions().httpsCallable(CREATE_FUNCTION);
  const { data } = await callable();
  return data;
};

/**
 * In-memory handoff for "Sign in with your phone": AuthPage opens the session behind
 * its button's spinner, and LinkDevicePage takes it, so the QR is there on arrival.
 * Module memory only, never storage or router state: the claimSecret must stay out
 * of anything persisted (see LinkDevicePage).
 */
let prefetchedSession = null;

export const prefetchDeviceLinkSession = async () => {
  prefetchedSession = await createDeviceLinkSession();
};

/** The prefetched session, once: a second call (or a stale tab) gets null. */
export const takePrefetchedDeviceLinkSession = () => {
  const session = prefetchedSession;
  prefetchedSession = null;
  return session;
};

export const approveDeviceLinkSession = async (sessionId) => {
  const callable = firebaseApp.functions().httpsCallable(APPROVE_FUNCTION);
  const { data } = await callable({ sessionId });
  return data;
};

export const claimDeviceLinkSession = async ({ sessionId, claimSecret }) => {
  const callable = firebaseApp.functions().httpsCallable(CLAIM_FUNCTION);
  const { data } = await callable({ sessionId, claimSecret });
  return data;
};

/**
 * The GRANT half: a signed-in device lends its account to one that scans.
 *
 * Opposite direction to the session half above. The secret travels IN the QR alongside the id,
 * which is what removes the confirmation step entirely — the scanning phone arrives already
 * holding everything it needs and signs itself in before the person has lowered their arm.
 *
 * ⚠️ SO THE QR IS A BEARER CREDENTIAL WHILE IT IS UNSPENT. What bounds it is that it is spent
 * on FIRST use and deleted before the token is minted: whoever scans first wins, and in
 * practice that is the clinician holding the phone, milliseconds later. Plus a two-minute TTL.
 * The residual risk is someone who photographs the screen AND redeems it before the clinician's
 * own scan lands. Nothing here can make an unattended screen safe — see deviceLink.js.
 */
export const createDeviceLinkGrant = async () => {
  const callable = firebaseApp.functions().httpsCallable(CREATE_GRANT_FUNCTION);
  const { data } = await callable();
  return data;
};

export const redeemDeviceLinkGrant = async ({ grantId, grantSecret }) => {
  const callable = firebaseApp.functions().httpsCallable(REDEEM_GRANT_FUNCTION);
  const { data } = await callable({ grantId, grantSecret });
  return data;
};

export const signInWithDeviceLinkToken = (customToken) =>
  firebaseApp.auth().signInWithCustomToken(customToken);
